import React, { useEffect, useRef } from 'react';
import { X, Minimize2, Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Notion-style Side Panel Component
 * Opens from the right side of the screen with smooth animation
 * Can be minimized/expanded
 */
export default function SidePanel({ 
  isOpen, 
  onClose, 
  title, 
  subtitle,
  children, 
  width = 'max-w-2xl',
  minimized = false,
  onMinimize,
  showMinimize = true,
  className = ''
}) {
  const panelRef = useRef(null);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Don't prevent body scroll - allow interaction with main content (Notion-style)
  // Body scroll remains enabled so users can scroll the main page while panel is open

  if (!isOpen) return null;

  // Minimized state - show small floating bar at bottom right
  if (minimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-2xl border border-stone-200 p-3 cursor-pointer hover:shadow-xl transition-all"
        onClick={() => onMinimize && onMinimize(false)}
        data-testid="side-panel-minimized"
      >
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium text-stone-900">{title}</span>
          <Maximize2 size={16} className="text-stone-500" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* No backdrop - allows interaction with main content (Notion-style) */}
      
      {/* Panel */}
      <div 
        ref={panelRef}
        className={cn(
          "fixed top-0 right-0 h-full bg-white shadow-2xl z-50 flex flex-col",
          "transform transition-transform duration-300 ease-out",
          "animate-in slide-in-from-right",
          "border-l border-stone-200",
          width,
          "w-full sm:w-[90vw] md:w-[80vw] lg:w-[65vw] xl:w-[55vw]",
          className
        )}
        data-testid="side-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 bg-stone-50">
          <div>
            <h2 className="text-xl font-semibold text-stone-900">{title}</h2>
            {subtitle && <p className="text-sm text-stone-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {showMinimize && onMinimize && (
              <button
                onClick={() => onMinimize(true)}
                className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
                title="Minimize"
              >
                <Minimize2 size={18} className="text-stone-600" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
              title="Close"
            >
              <X size={18} className="text-stone-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </>
  );
}

// Sub-components for consistent styling
export function SidePanelSection({ title, children, className = '' }) {
  return (
    <div className={cn("space-y-3", className)}>
      {title && (
        <h3 className="text-sm font-medium text-stone-700 uppercase tracking-wide">{title}</h3>
      )}
      {children}
    </div>
  );
}

export function SidePanelDivider() {
  return <hr className="my-4 border-stone-200" />;
}

export function SidePanelActions({ children, className = '' }) {
  return (
    <div className={cn("flex items-center gap-3 pt-4 border-t border-stone-200 mt-6", className)}>
      {children}
    </div>
  );
}
